import { CircleAlert, CircleCheck, CircleOff, DownloadCloud, Monitor, Store } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import { ConnectionLine } from '../../../shared/ui/ConnectionLine'
import { QueryState } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import type { StructureActions, StructureVm } from '../model/types'
import './structure.css'

const PRESENCE_ICONS: Readonly<Record<'ok' | 'missing' | 'invalid', ReactNode>> = {
  ok: <CircleCheck size={14} aria-hidden="true" />,
  missing: <CircleOff size={14} aria-hidden="true" />,
  invalid: <CircleAlert size={14} aria-hidden="true" />,
}

/**
 * «Структура AQSI»: магазины кабинета → кассы каждого (п. 9 таблицы замечаний 13.10.2026), как дерево 1С
 * «организация → подразделение → склад». Данные — снимок касс службы; «Загрузить кассы из кабинета» — та же
 * команда, что в «Номенклатуре». Магазин сворачивается своим заголовком (`details`), без своего состояния.
 */
export function StructureScreen({ vm, actions }: { vm: StructureVm; actions: StructureActions }) {
  return (
    <section className="aqsi-screen" aria-labelledby="aqsi-structure-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-structure-title">Структура AQSI</h2>
        <Button
          icon={<DownloadCloud size={14} aria-hidden="true" />}
          busy={vm.refreshing}
          disabled={vm.busy}
          onClick={actions.refresh}
        >
          Загрузить кассы из кабинета
        </Button>
      </header>
      {vm.notice !== null && (
        <p className="aqsi-caption" role="status" data-kind={vm.notice.kind}>
          {vm.notice.kind === 'done' ? <CircleCheck size={14} aria-hidden="true" /> : <CircleAlert size={14} aria-hidden="true" />}{' '}
          {vm.notice.text}
        </p>
      )}
      {vm.snapshot !== '' && <p className="aqsi-caption">{vm.snapshot}</p>}
      <QueryState query={vm.query} what="Кассы" emptyText="В снимке касс нет" onRetry={actions.retry} />
      {vm.summary !== null && vm.shops.length > 0 && (
        <>
          <p className="aqsi-caption">{vm.summary}</p>
          <ul className="aqsi-structure" aria-label="Структура кабинета">
            {vm.shops.map((shop) => (
              <li key={shop.id} className="aqsi-structure__shop">
                <details open>
                  <summary className="aqsi-structure__summary">
                    <Store size={16} aria-hidden="true" />
                    <span className="aqsi-structure__name">{shop.name}</span>
                    <span className="aqsi-caption">· касс: {shop.devices.length}</span>
                  </summary>
                  <ul className="aqsi-structure__devices" aria-label={`Кассы магазина «${shop.name}»`}>
                    {shop.devices.map((device) => (
                      <li key={device.deviceId} className="aqsi-structure__device" data-state={device.stateKind}>
                        <p className="aqsi-card__title">
                          <Monitor size={16} aria-hidden="true" />
                          Касса {device.serial}
                        </p>
                        <p className="aqsi-card__text">
                          {device.model} · СНО: {device.taxSystem}
                        </p>
                        <span className="aqsi-card__state">
                          {PRESENCE_ICONS[device.stateKind]}
                          {device.state}
                        </span>
                        <ConnectionLine connection={device.connection} />
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
